"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, BookOpen, GraduationCap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const categories = [{
  id: "oficios",
  label: "Oficios"
}, {
  id: "estetica",
  label: "Estética"
}, {
  id: "salud",
  label: "Bienestar y Salud"
}, {
  id: "deportes",
  label: "Deportes"
}, {
  id: "empresariales",
  label: "Empresariales"
}];
const courses = [{
  categoryId: "oficios",
  title: "Técnico Electricista",
  modality: "Presencial",
  link: "/tecnico-electricista",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/electrical_technician_course.png"
}, {
  categoryId: "oficios",
  title: "Técnico en Aires Acondicionados",
  modality: "Presencial",
  link: "/tecnico-en-aires-acondicionados",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/ac_technician_training.png"
}, {
  categoryId: "oficios",
  title: "Supervisor Forestal",
  modality: "Híbrido",
  link: "/supervisor-forestal",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/forestry_supervisor_training.png"
}, {
  categoryId: "oficios",
  title: "Técnico en Electrónica",
  modality: "Presencial",
  link: "/tecnico-en-electronica",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/electronics_technician_course.png"
}, {
  categoryId: "oficios",
  title: "Técnico en Energía Solar",
  modality: "Híbrido",
  link: "/tecnico-en-energia-solar",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/solar_energy_training.png"
}, {
  categoryId: "oficios",
  title: "Presupuestación de Obras",
  modality: "Híbrido",
  link: "/presupuestacion-de-obras",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/construction_budgeting_course.png"
}, {
  categoryId: "oficios",
  title: "Producción Avícola",
  modality: "Presencial",
  link: "/produccion-avicola",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/poultry_production_training.png"
}, {
  categoryId: "estetica",
  title: "Barbería",
  modality: "Presencial",
  link: "/barberia",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/barbershop_training_course.png"
}, {
  categoryId: "estetica",
  title: "Extensiones de Pestañas",
  modality: "Presencial",
  link: "/extensiones-en-pestanas",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/eyelash_extensions_course.png"
}, {
  categoryId: "salud",
  title: "Masoterapia",
  modality: "Presencial",
  link: "/masoterapia",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/massage_therapy_training.png"
}, {
  categoryId: "salud",
  title: "Primeros Auxilios",
  modality: "Presencial",
  link: "/primeros-auxilios",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/first_aid_course.png"
}, {
  categoryId: "salud",
  title: "Reflexología Podal",
  modality: "Presencial",
  link: "/reflexologia-podal",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/foot_reflexology_training.png"
}, {
  categoryId: "salud",
  title: "Auriculoterapia",
  modality: "Híbrido",
  link: "/auriculoterapia",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/auriculotherapy_training.png"
}, {
  categoryId: "deportes",
  title: "Kinesiología Deportiva",
  modality: "Presencial",
  link: "/kinesiologia-deportiva",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/sports_kinesiology_course.png"
}, {
  categoryId: "deportes",
  title: "Entrenador Integral",
  modality: "Híbrido",
  link: "/entrenador-integral",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/holistic_fitness_trainer.png"
}, {
  categoryId: "deportes",
  title: "Entrenamiento Funcional",
  modality: "Presencial",
  link: "/entrenamiento-funcional",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/functional_training_course.png"
}, {
  categoryId: "deportes",
  title: "Instructor en Sala de Musculación",
  modality: "Presencial",
  link: "/instructor-en-sala-de-musculacion",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/gym_instructor_training.png"
}, {
  categoryId: "empresariales",
  title: "Auxiliar Administrativo Contable",
  modality: "Híbrido",
  link: "/auxiliar-administrativo-contable",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/accounting_assistant_course.png"
}, {
  categoryId: "empresariales",
  title: "Legislación Laboral y Liquidación de Sueldos",
  modality: "Híbrido",
  link: "/legislacion-laboral-y-liquidacion-de-sueldos",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/labor_law_payroll_training.png"
}, {
  categoryId: "empresariales",
  title: "Excel",
  modality: "Híbrido",
  link: "/excel",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/excel_proficiency_course.png"
}, {
  categoryId: "empresariales",
  title: "Conducción, Locución y Streaming",
  modality: "Presencial",
  link: "/conduccion-locucion-y-streaming",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/broadcasting_streaming_course.png"
}];
export default function TrainingAreas() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  return <section id="áreas-de-capacitación" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-12 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-full mb-4">
            <GraduationCap className="w-6 h-6 text-primary" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-4">
            Áreas de Capacitación
          </h2>
          <p className="text-lg text-muted-foreground">
            Explora nuestro catálogo completo de cursos organizados por áreas de especialización. Formación práctica y actualizada para impulsar tu carrera en todo Uruguay.
          </p>
        </div>

        <div ref={contentRef} className={`transition-all duration-1000 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Tabs defaultValue="oficios" className="w-full flex flex-col items-center">
            <TabsList className="h-auto flex-wrap justify-center gap-2 p-1.5 bg-muted/50 backdrop-blur-sm mb-8 max-w-full">
              {categories.map((category, index) => <TabsTrigger key={category.id} value={category.id} data-index={index} className="text-sm md:text-base px-4 py-2.5 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all">
                  {category.label}
                </TabsTrigger>)}
            </TabsList>

            <div className="w-full max-w-7xl mx-auto">
              {categories.map((category, index) => <TabsContent key={category.id} value={category.id} data-index={index} className="mt-0 outline-none focus-visible:ring-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {courses.filter(course => course.categoryId === category.id).map((course, courseIndex) => <div key={courseIndex} data-index={courseIndex} className="h-full">
                          <Card className="group relative h-[450px] rounded-3xl overflow-hidden bg-card shadow-2xl border border-border/50 transition-all duration-700 ease-out cursor-pointer p-0 gap-0">
                            <img src={course.image} alt={course.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-in-out group-hover:scale-110" />
                            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 group-hover:from-background/90 group-hover:via-background/60" />

                            <div className="absolute inset-0 p-8 flex flex-col justify-end">
                              <div className="transform transition-transform duration-500 ease-out group-hover:-translate-y-2">
                                <CardHeader className="p-0 mb-4 flex flex-row justify-between items-center space-y-0">
                                  <Badge variant="secondary" className="bg-secondary/20 text-white backdrop-blur-md border-transparent font-medium px-3 py-1">
                                    {course.modality}
                                  </Badge>
                                  <div className="p-2 bg-white/10 backdrop-blur-md rounded-lg">
                                    <BookOpen className="w-5 h-5 text-white" />
                                  </div>
                                </CardHeader>

                                <CardTitle className="text-2xl font-extrabold text-white mb-4 leading-tight tracking-tight uppercase">
                                  {course.title}
                                </CardTitle>

                                <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-500 ease-in-out">
                                  <div className="overflow-hidden">
                                    <CardFooter className="p-0 pt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                                      <Button asChild variant="outline" size="lg" className="w-full border-primary/30 text-white group-hover:bg-yellow-400 rounded-xl font-bold py-6 transition-all duration-300">
                                        <Link to={course.link} newTab={course.link.startsWith("http")}>
                                          Ver más
                                          <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                                        </Link>
                                      </Button>
                                    </CardFooter>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Card>
                        </div>)}
                  </div>
                </TabsContent>)}
            </div>
          </Tabs>
        </div>
      </div>
    </section>;
}